import React, { useEffect, useState } from "react";
import { EmployeeInfo } from "../interfaces/EmployeeInfoInterface";
import EmployeeManager from "../managers/EmployeeManager";
import TimesheetManager from "../managers/TimesheetManager";
import NotificationModal from "./NotificationModal";
import { useSiteContext } from "../SiteContext";

interface ChangeSupervisorModalProps {
  visible: boolean;
  onClose: () => void;
  employeeName: string;
  employeeAltID: string;
  currentSupervisorName: string;
  currentSupervisorAltID: string;
  timesheetID: number;
  userID: number;
}

const ChangeSupervisorModal: React.FC<ChangeSupervisorModalProps> = ({
  visible,
  onClose,
  employeeName,
  employeeAltID,
  currentSupervisorName,
  currentSupervisorAltID,
  timesheetID,
  userID
}) => {
  const { setLoading, loading } = useSiteContext();
  const [supervisors, setSupervisors] = useState<EmployeeInfo[]>([]);
  const [localLoading, setLocalLoading] = useState(false);
  const [minLoading, setMinLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notification, setNotification] = useState<{
    visible: boolean;
    type: "success" | "error";
    title: string;
    message: string;
  }>({
    visible: false,
    type: "success",
    title: "",
    message: ""
  });

  // Ensure spinner shows for at least 0.5s
  const showMinLoading = () => {
    setMinLoading(true);
    setTimeout(() => setMinLoading(false), 500);
  };

  // Fetch supervisors when modal opens
  useEffect(() => {
    if (visible) {
      fetchSupervisors();
    }
  }, [visible]);

  const fetchSupervisors = async () => {
    setLocalLoading(true);
    showMinLoading();
    setError(null);
    try {
      const supervisorsData = await EmployeeManager.getSupervisors();
      // Sort supervisors alphabetically by first name, then last name
      const sortedSupervisors = supervisorsData.sort((a, b) => {
        const nameA = `${a.firstName} ${a.lastName}`.toLowerCase();
        const nameB = `${b.firstName} ${b.lastName}`.toLowerCase();
        return nameA.localeCompare(nameB);
      });
      setSupervisors(sortedSupervisors);
    } catch (error) {
      console.error("Error fetching supervisors:", error);
      setError("Failed to fetch supervisors. Please try again.");
    } finally {
      // Wait for minLoading to finish if needed
      setTimeout(() => setLocalLoading(false), minLoading ? 500 : 0);
    }
  };

  const handleSelectSupervisor = async (supervisor: EmployeeInfo) => {
    try {
      setLoading(true);
      const result = await TimesheetManager.updateTimesheetSupervisor(
        timesheetID,
        supervisor.employeeID,
        userID
      );
      
      console.log("Supervisor update result:", result);
      setNotification({
        visible: true,
        type: "success",
        title: "Supervisor Updated Successfully",
        message: `The supervisor has been changed to ${supervisor.firstName} ${supervisor.lastName} - ${supervisor.altEmployeeID}`
      });
    } catch (error) {
      console.error("Error updating supervisor:", error);
      setNotification({
        visible: true,
        type: "error",
        title: "Update Failed",
        message: `Failed to update supervisor: ${error instanceof Error ? error.message : 'Unknown error'}`
      });
    } finally {
      setLoading(false);
    }
  };

  const handleNotificationClose = () => {
    setNotification(prev => ({ ...prev, visible: false }));
    if (notification.type === "success") {
      onClose(); // Close the main modal on success
    }
  };

  if (!visible) return null;

  return (
    <>
      <div className="change-supervisor-modal-overlay" onClick={onClose}>
        <div className="change-supervisor-modal" onClick={(e) => e.stopPropagation()}>
          <h2 className="change-supervisor-title">
            Change Supervisor
          </h2>

          <div className="change-supervisor-content">
            <p><strong>Employee:</strong> <strong>{employeeName.replace(' - ', ' ')} - {employeeAltID}</strong></p>
            <p><strong>Current Supervisor:</strong> <strong>{currentSupervisorName.replace(' - ', ' ')} - {currentSupervisorAltID}</strong></p>
            
            <div className="supervisor-selection-section">
              <h3>Select New Supervisor:</h3>
              
              {(localLoading || minLoading) ? (
                <div className="loading-spinner-container">
                  <div className="loading-spinner" />
                </div>
              ) : error ? (
                <div className="error-message">{error}</div>
              ) : supervisors.length === 0 ? (
                <div className="no-supervisors-message">No supervisors available.</div>
              ) : (
                <div className="supervisor-list-container">
                  <ul className="supervisor-list">
                    {supervisors.map((supervisor) => (
                      <li key={supervisor.employeeID} className="supervisor-item">
                        <div className="supervisor-info">
                          <span className="supervisor-name">
                            <strong>{supervisor.firstName} {supervisor.lastName} - {supervisor.altEmployeeID}</strong>
                          </span>
                        </div>
                        <button 
                          className="select-supervisor-button"
                          onClick={() => handleSelectSupervisor(supervisor)}
                          disabled={loading}
                        >
                          {loading ? "Updating..." : "Select"}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>

          <div className="change-supervisor-modal-footer">
            <button className="change-supervisor-button" onClick={onClose} disabled={loading}>
              Cancel
            </button>
          </div>
        </div>
      </div>

      <NotificationModal
        visible={notification.visible}
        onClose={handleNotificationClose}
        type={notification.type}
        title={notification.title}
        message={notification.message}
      />
    </>
  );
};

export default ChangeSupervisorModal;

// Add CSS styles directly in this file using a template literal
const styles = `
:root {
  --primary-color: #007bff;
  --primary-color-hover: #0056b3;
  --secondary-color: #f9f9f9;
}

.change-supervisor-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 999;
}

.change-supervisor-modal {
  background: #fff;
  padding: 30px;
  border-radius: 10px;
  width: 500px;
  max-width: 90%;
  max-height: 80vh;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
}

.change-supervisor-title {
  margin-top: 0;
  font-size: 24px;
  color: #333;
  text-align: center;
}

.change-supervisor-content {
  margin: 20px 0;
  color: #555;
  flex: 1;
  overflow-y: auto;
}

.change-supervisor-content p {
  margin: 10px 0;
  font-size: 16px;
}

.change-supervisor-content p strong {
  color: #000000;
  font-weight: bold;
}

.supervisor-selection-section {
  margin-top: 20px;
}

.supervisor-selection-section h3 {
  margin-bottom: 15px;
  color: #333;
  font-size: 18px;
}

.loading-message,
.error-message,
.no-supervisors-message {
  text-align: center;
  padding: 20px;
  color: #666;
  font-style: italic;
}

.error-message {
  color: #d32f2f;
  background-color: #fde8e8;
  border-radius: 4px;
  padding: 10px;
}

.supervisor-list-container {
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid #ddd;
  border-radius: 5px;
}

.supervisor-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.supervisor-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 15px;
  border-bottom: 1px solid #eee;
  transition: background-color 0.2s ease;
}

.supervisor-item:last-child {
  border-bottom: none;
}

.supervisor-item:hover {
  background-color: #f8f9fa;
}

.supervisor-info {
  display: flex;
  align-items: center;
}

.supervisor-name {
  color: #333;
  font-size: 14px;
}

.select-supervisor-button {
  padding: 6px 12px;
  background-color: var(--primary-color);
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  font-weight: bold;
  transition: background-color 0.2s ease;
}

.select-supervisor-button:hover {
  background-color: var(--primary-color-hover);
}

.change-supervisor-modal-footer {
  display: flex;
  justify-content: center;
  gap: 12px;
  margin-top: 20px;
  padding-top: 15px;
  border-top: 1px solid #eee;
}

.change-supervisor-button {
  padding: 10px 20px;
  background-color: #ccc;
  color: black;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: bold;
}

.change-supervisor-button:hover {
  background-color: #999;
}
`;

// Add styles to document head
const styleTag = document.createElement('style');
styleTag.innerHTML = styles;
document.head.appendChild(styleTag);

/* Add spinner styles at the bottom of the file */
const spinnerStyles = `
.loading-spinner-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 60px;
}
.loading-spinner {
  border: 6px solid #f3f3f3;
  border-top: 6px solid #007bff;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
}
@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
`;
if (typeof window !== 'undefined' && !document.getElementById('change-supervisor-spinner-style')) {
  const styleTag = document.createElement('style');
  styleTag.id = 'change-supervisor-spinner-style';
  styleTag.innerHTML = spinnerStyles;
  document.head.appendChild(styleTag);
} 